import { ampli } from '@/ampli';
import { useEffect } from 'react';
import { ContractFunctionExecutionError, erc20Abi, maxUint256, parseUnits } from 'viem';
import { useSwitchChain, type UseWriteContractReturnType } from 'wagmi';

import { ClientWalletError } from '@/lib/errors/client';

import { signIntentsApproval } from '@/components/TransactionModule/utils';

import { NETWORK_NAME_PER_CHAIN_ID, SUPPORTED_CHAINS_BY_CHAIN_ID } from '@/constants/chain';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { useApprove } from '@/hooks/useApprove';
import { useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { toAmpliSwapOrderType } from '@/utils/advancedOrderAnalytics';
import { trackError } from '@/utils/errors';
import type { IntentsSubmitApproval } from '@/utils/intents/types';
import type { EthereumAddress, EthereumHash } from '@/utils/models';
import {
  getConnectedChain,
  getPrivyWalletClient,
  isUserRejection,
  reportWalletGuardDiagnostics,
  trySwitchChain,
} from '@/utils/wallet';

import { sellInputSelector, useTradeModuleStore } from '../store';
import type { ExecutionContext } from './types';

/** Result of submitting a market token approval. */
export type ApprovalActionOutcome =
  | { status: 'submitted' }
  | { status: 'rejected' }
  | { status: 'aborted' }
  | { status: 'failed'; error: Error };

/** Result of signing an intents permit. */
export type SignIntentsApprovalOutcome =
  | { status: 'signed'; approval: IntentsSubmitApproval }
  | Exclude<ApprovalActionOutcome, { status: 'submitted' }>;

/** Result of submitting a cross-chain token approval. */
export type CrossChainApprovalOutcome =
  | { status: 'submitted'; hash: EthereumHash }
  | Exclude<ApprovalActionOutcome, { status: 'submitted' }>;

type ApprovalFailureOutcome = { status: 'rejected' } | { status: 'failed'; error: Error };

function approvalFailureOutcome(error: unknown): ApprovalFailureOutcome {
  if (isUserRejection(error)) return { status: 'rejected' };
  const normalizedError = error instanceof Error ? error : new Error(String(error));
  trackError(normalizedError);
  return { status: 'failed', error: normalizedError };
}

function parseSellInputAmount(decimal: string, decimals: number | undefined) {
  if (!decimal || decimals === undefined) return undefined;
  try {
    return parseUnits(decimal, decimals);
  } catch {
    return undefined;
  }
}

/** Reactive market-token approval action and receipt state for the rewrite view. */
export function useApproveSellToken({
  allowanceTarget,
  sellToken,
}: {
  allowanceTarget: EthereumAddress | undefined;
  sellToken: TokenResult | undefined;
}) {
  const { ethereumWallet } = useMatchaWallets();
  const [checkoutMode] = useCheckoutMode();
  const [advancedOrderType] = useAdvancedOrderType();
  const sellInput = useTradeModuleStore(sellInputSelector);
  const { switchChainAsync } = useSwitchChain();

  const requiredAmount = parseSellInputAmount(sellInput.decimal, sellToken?.decimals);
  const approval = useApprove({
    spender: allowanceTarget,
    token: sellToken,
    requiredAmount,
  });
  const connectedEthereumChain = getConnectedChain(ethereumWallet);

  useEffect(() => {
    const error = approval.approvalError;
    if (!(error instanceof ContractFunctionExecutionError) || isUserRejection(error)) return;
    trackError(error, {
      chain: connectedEthereumChain?.name,
      address: sellToken?.address,
    });
  }, [approval.approvalError, connectedEthereumChain?.name, sellToken?.address]);

  useEffect(() => {
    if (approval.approvalReceipt?.status !== 'success' || !sellToken) return;
    ampli.tokenApproved({
      is_gasless_approval: false,
      swap_order_type: toAmpliSwapOrderType(checkoutMode, advancedOrderType),
      sell_token_address: sellToken.address,
      sell_token_amount: parseFloat(sellInput.decimal) || 0,
      sell_token_chain_id: sellToken.chainId,
      sell_token_chain_name: NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId],
      sell_token_symbol: sellToken.symbol,
      sell_token_amount_usd: sellInput.usd,
    });
  }, [approval.approvalReceipt, advancedOrderType, checkoutMode, sellInput, sellToken]);

  const approve = async (): Promise<ApprovalActionOutcome> => {
    if (!sellToken || !allowanceTarget) return { status: 'aborted' };
    try {
      if (getConnectedChain(ethereumWallet)?.id !== sellToken.chainId) {
        const result = await trySwitchChain({
          chainId: sellToken.chainId,
          switchChainAsync,
        });
        if (!result.success) return { status: 'aborted' };
      }
      ampli.tokenApproveInitiated({
        is_gasless_approval: false,
        swap_order_type: toAmpliSwapOrderType(checkoutMode, advancedOrderType),
        sell_token_address: sellToken.address,
        sell_token_amount: parseFloat(sellInput.decimal) || 0,
        sell_token_amount_usd: sellInput.usd || 0,
        sell_token_chain_id: sellToken.chainId,
        sell_token_chain_name: NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId],
        sell_token_symbol: sellToken.symbol,
      });
      approval.approve();
      return { status: 'submitted' };
    } catch (error: unknown) {
      return approvalFailureOutcome(error);
    }
  };

  return {
    approve,
    isApproving: approval.isApproving,
    isPendingApproval: approval.isPendingApproval,
    needsAllowanceReset: approval.needsAllowanceReset,
  };
}

/** Signs the current intents permit without advancing the submit checkout machine. */
export async function signIntentsApprovalAction(
  ctx: ExecutionContext,
): Promise<SignIntentsApprovalOutcome> {
  if (
    ctx.quote.source !== 'intents' ||
    !ctx.quote.raw ||
    !('allowanceTarget' in ctx.quote.raw) ||
    !('approval' in ctx.quote.raw) ||
    !ctx.quote.raw.approval ||
    !ctx.ethereumWallet
  ) {
    return { status: 'aborted' };
  }
  try {
    if (getConnectedChain(ctx.ethereumWallet)?.id !== ctx.chainId) {
      const result = await trySwitchChain({
        chainId: ctx.chainId,
        switchChainAsync: ctx.switchChainAsync,
      });
      if (!result.success) return { status: 'aborted' };
    }
    const chain = SUPPORTED_CHAINS_BY_CHAIN_ID[ctx.chainId];
    const client = chain ? await getPrivyWalletClient(ctx.ethereumWallet, chain) : undefined;
    if (!client) {
      reportWalletGuardDiagnostics({
        ethereumWallet: ctx.ethereumWallet,
        targetChainId: ctx.sellToken.chainId,
        accountAddress: ctx.accountAddress,
        walletClientSource: 'privy_direct',
      });
      throw new ClientWalletError('Wallet not connected');
    }
    const approval = ctx.quote.raw.approval;
    const signedApproval = await signIntentsApproval({
      type: approval.type,
      eip712: approval.eip712,
      walletClient: client,
    });
    ampli.tokenApproved({
      is_gasless_approval: true,
      sell_token_address: ctx.sellToken.address,
      sell_token_amount: parseFloat(ctx.sellInput.decimal) || 0,
      sell_token_chain_id: ctx.sellToken.chainId,
      sell_token_chain_name: NETWORK_NAME_PER_CHAIN_ID[ctx.sellToken.chainId],
      sell_token_symbol: ctx.sellToken.symbol,
      sell_token_amount_usd: ctx.sellInput.usd,
      swap_order_type: 'market',
    });
    return { status: 'signed', approval: signedApproval };
  } catch (error: unknown) {
    return approvalFailureOutcome(error);
  }
}

/**
 * Approves the cross-chain quote's spender for the sell token: an unlimited allowance when the
 * user's max-approval setting is on, otherwise exactly the quote's sell amount. Smart wallets
 * always get the exact amount, since the max-approval toggle is hidden for them.
 */
export async function approveCrossChainSpend(
  ctx: ExecutionContext,
  {
    writeContractAsync,
    maxApproval,
  }: {
    writeContractAsync: UseWriteContractReturnType['writeContractAsync'];
    /** The user's max-approval setting; see `useApprovalSettingsStore`. */
    maxApproval: boolean;
  },
): Promise<CrossChainApprovalOutcome> {
  if (
    ctx.quote.source !== 'cross-chain' ||
    !ctx.quote.raw ||
    ctx.quote.raw.transaction.chainType !== 'evm' ||
    !ctx.quote.raw.issues?.allowance ||
    !ctx.ethereumWallet
  ) {
    return { status: 'aborted' };
  }
  try {
    if (getConnectedChain(ctx.ethereumWallet)?.id !== ctx.chainId) {
      const result = await trySwitchChain({
        chainId: ctx.chainId,
        switchChainAsync: ctx.switchChainAsync,
      });
      if (!result.success) return { status: 'aborted' };
    }

    const spender = ctx.quote.raw.issues.allowance.spender;
    ampli.tokenApproveInitiated({
      is_gasless_approval: false,
      swap_order_type: 'cross_chain',
      sell_token_address: ctx.sellToken.address,
      sell_token_amount: parseFloat(ctx.sellInput.decimal) || 0,
      sell_token_amount_usd: ctx.sellInput.usd || 0,
      sell_token_chain_id: ctx.sellToken.chainId,
      sell_token_chain_name: NETWORK_NAME_PER_CHAIN_ID[ctx.sellToken.chainId],
      sell_token_symbol: ctx.sellToken.symbol || '',
    });

    const approvedAmount =
      maxApproval && !ctx.isSmartWallet ? maxUint256 : BigInt(ctx.quote.raw.sellAmount);
    const hash = await writeContractAsync({
      address: ctx.sellToken.address as EthereumAddress,
      abi: erc20Abi,
      functionName: 'approve',
      args: [spender, approvedAmount],
    });
    return { status: 'submitted', hash };
  } catch (error: unknown) {
    return approvalFailureOutcome(error);
  }
}
